import React, { useEffect, useState } from "react";
import Select, { SingleValue } from "react-select";
import TaskCreationLKValues from "../../../../interfaces/lkValues/TaskCreationLKValues";
import { useAuth } from "../../../../firebase/AuthProvider";
import { getTaskLKValues } from "../../../../services/taskServices/TaskLKService";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faAngleDown,
    faAngleUp,
    faAnglesUp,
} from "@fortawesome/free-solid-svg-icons";
import { publishTask } from "../../../../services/taskServices/TaskService";
import NewTask from "../../../../interfaces/task/NewTask";
import { useNavigate } from "react-router-dom";
import {
    GetAllParticipantUnderMeAsync,
} from "../../../../services/participantServices/ParticipantService";
import TaskWorkflowGroup from "../../../../interfaces/task/workflow/TaskWorkflowGroup";
import { getWorkflowGroups } from "../../../../services/taskServices/TaskWorkflowServices";
import { Button, Form, Row, Col, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { ROLES } from "../../../../Data/SD";

interface OptionType {
    value: string;
    label: string;
}

const TaskCreation: React.FC = () => {
    const { currentUser, currentOrganization, currentRoles } = useAuth();
    const navigate = useNavigate();

    const [isSC, setIsSC] = useState<boolean>(false);

    const [ready, setReady] = useState<boolean>(false);
    const [taskOrganizationOptions, setTaskOrganizationOptions] = useState<
        OptionType[]
    >([]);
    const [participantOptions, setParticipantOptions] = useState<OptionType[]>(
        []
    );
    const [taskTypeOptions, setTaskTypeOptions] = useState<OptionType[]>([]);
    const [taskSubTypeOptions, setTaskSubTypeOptions] = useState<OptionType[]>(
        []
    );
    const [assigneeOptions, setAssigneeOptions] = useState<OptionType[]>([]);
    const [workflowGroupOptions, setWorkflowGroupOptions] = useState<
        OptionType[]
    >([]);
    const [requiredFilesOptions, setRequiredFilesOptions] = useState<
        OptionType[]
    >([]);

    const [newTask, setNewTask] = useState<NewTask>({
        title: "",
        description: "",
        requiredActions: "",
        requiredFiles: [],
        assignee: undefined,
        coAssignee: undefined,
        participant: undefined,
        taskOrganization: "1",
        taskType: "1",
        taskSubType: "4",
        dueDate: "",
        workflowGroup: "Standard",
        priority: "Low",
    });

    // spinner / submitting state
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [loading, setLoading] = useState<{ participant: boolean; assignee: boolean }>({
        participant: false,
        assignee: false,
    });

    const priorityOptions = [
        {
            value: "High",
            label: (
                <span>
                    <FontAwesomeIcon className="text-danger" icon={faAnglesUp} /> High
                </span>
            ),
        },
        {
            value: "Medium",
            label: (
                <span>
                    <FontAwesomeIcon icon={faAngleUp} className="text-warning" /> Medium
                </span>
            ),
        },
        {
            value: "Low",
            label: (
                <span>
                    <FontAwesomeIcon icon={faAngleDown} className="text-ready" /> Low
                </span>
            ),
        },
    ];

    const [lkOptions, setLkOptions] = useState<
        TaskCreationLKValues | null | undefined
    >();
    // const [participants, setParticipants] = useState<
    //     Participant[] | null | undefined
    // >();
    const [workflowGroups, setWorkflowGroups] =
        useState<TaskWorkflowGroup | null>();

    // errors keyed by field name
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [validationError, setValidationError] = useState<string | null>(null);

    useEffect(() => {
        // Fetch the task with taskId and update the state
        const fetchLkValues = async () => {
            if (currentUser !== null) {
                setLoading({ participant: true, assignee: true });
                const authToken: string = await currentUser.getIdToken();
                const fetchedValues: TaskCreationLKValues | null =
                    await getTaskLKValues(authToken);
                // const fetchedParticipants: Participant[] | null =
                //     await getAllParticipants(authToken);
                const fetchedWorkflowGroups: TaskWorkflowGroup | null =
                    await getWorkflowGroups(authToken);
                setLkOptions(fetchedValues);
                // setParticipants(fetchedParticipants);
                setWorkflowGroups(fetchedWorkflowGroups);
                setLoading({ participant: false, assignee: false });
            }
        };

        const isSCHighestRole = currentRoles?.length === 1 && currentRoles[0].name === ROLES.SC ? true : false;
        setIsSC(isSCHighestRole);

        if (lkOptions) {
            const formattedTaskOrganizations = lkOptions.taskOrganizations.map(
                (org) => ({
                    value: org.id.toString(),
                    label: org.type,
                })
            );
            const formattedTaskTypes = lkOptions.taskTypes.map((tt) => ({
                value: tt.id.toString(),
                label: tt.type,
            }));
            const formattedTaskSubTypes = lkOptions.taskSubTypes.map((tst) => ({
                value: tst.id.toString(),
                label: tst.type,
            }));
            const formattedAssignees = lkOptions.users.map((user) => ({
                value: user.id.toString(),
                label: user.displayName,
            }));
            // const formattedParticipants = participants?.map((part) => ({
            //     value: part.id.toString(),
            //     label: part.firstName + " " + part.lastName,
            // }));
            if (workflowGroups !== undefined && workflowGroups !== null)
                mapTaskWorkflowGroupToOptions(workflowGroups);
            setTaskSubTypeOptions(formattedTaskSubTypes);
            if (isSCHighestRole && currentUser) {
                setAssigneeOptions([{
                    value: currentUser?.uid,
                    label: currentUser?.displayName ? currentUser.displayName : 'Assign to yourself',
                }])
            } else {
                setAssigneeOptions(formattedAssignees);
            }
            setTaskTypeOptions(formattedTaskTypes);
            setTaskOrganizationOptions(formattedTaskOrganizations);
            // if (formattedParticipants) {
            //     setParticipantOptions(formattedParticipants);
            // }
        }
        if (!lkOptions) {
            fetchLkValues();
        }
    }, [lkOptions]);


    // useEffect for changing the assignee
    useEffect(() => {
        getAllParticipantUnderMe();
    }, [newTask.assignee]);

    function mapTaskWorkflowGroupToOptions(taskWorkflowGroup: TaskWorkflowGroup) {
        let options: { value: string; label: string }[] = [];

        // Iterate over each flowGroup and its elements
        for (const flowGroup in taskWorkflowGroup) {
            const label = `${flowGroup}`;
            options.push({ value: flowGroup.toString(), label: label });
        }

        setWorkflowGroupOptions(options);
    }

    const validateField = (name: string, value: any) => {
        let message = "";
        switch (name) {
            case "title":
                if (!value || value.trim() === "") message = "Title is required.";
                break;
            case "dueDate":
                if (!value) message = "Due date is required.";
                break;
            case "description":
                if (!value || value.trim() === "") message = "Description is required.";
                break;
            case "assignee":
                if (!value) message = "Assignee is required.";
                break;
            case "participant":
                if (!value) message = "Participant is required.";
                break;
            default:
                message = "";
        }
        setErrors((prev) => ({ ...prev, [name]: message }));
        return message === "";
    };

    const validateAll = () => {
        const fieldChecks = [
            validateField("title", newTask.title),
            validateField("dueDate", newTask.dueDate),
            validateField("description", newTask.description),
            validateField("assignee", newTask.assignee),
            validateField("participant", newTask.participant),
        ];
        const ok = fieldChecks.every(Boolean);
        setReady(ok);
        return ok;
    };

    const handleTaskOrganizationChange = (
        selectedOption: SingleValue<OptionType>
    ) => {
        const taskOrganization = selectedOption ? selectedOption.value : "";
        setNewTask((prev) => ({ ...prev, taskOrganization }));
    };

    const handleWorkflowGroupChange = (
        selectedOption: SingleValue<OptionType>
    ) => {
        const workflowGroup = selectedOption ? selectedOption.value : "";
        setNewTask((prev) => ({ ...prev, workflowGroup }));
    };

    const handleTaskTypeChange = (selectedOption: SingleValue<OptionType>) => {
        const taskType = selectedOption ? selectedOption.value : "";
        setNewTask((prev) => ({ ...prev, taskType }));
    };

    const handleTaskSubTypeChange = (selectedOption: SingleValue<OptionType>) => {
        const taskSubType = selectedOption ? selectedOption.value : "";
        setNewTask((prev) => ({ ...prev, taskSubType }));
    };

    const handleAssigneeChange = (selectedOption: SingleValue<OptionType>) => {
        const assignee = selectedOption ? selectedOption.value : "";
        setNewTask((prev) => ({
            ...prev,
            assignee,
            participant: undefined, // Clear participant when assignee changes
        }));
        setParticipantOptions([]); // Clear participant options immediately
        validateField("assignee", assignee);
    };

    const handleParticipantChange = (selectedOption: SingleValue<OptionType>) => {
        const participant = selectedOption ? selectedOption.value : "";
        setNewTask((prev) => ({ ...prev, participant }));
        validateField("participant", participant);
    };

    const handleCoAssigneeChange = (selectedOption: SingleValue<OptionType>) => {
        const coAssignee = selectedOption ? selectedOption.value : "";
        setNewTask((prev) => ({ ...prev, coAssignee }));
    };

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setNewTask((prev) => ({ ...prev, [name]: value }));
        validateField(name, value);
    };

    const handleRequiredFileChanges = (selectedOptions: OptionType[]) => {
        const requiredFiles = selectedOptions
            ? selectedOptions.map((option) => option.value)
            : [];
        setNewTask((prev) => ({ ...prev, requiredFiles }));
    };

    const handleCustomChange = (
        fieldName: keyof NewTask,
        fieldValue: string | undefined
    ) => {
        setNewTask((prev) => ({ ...prev, [fieldName]: fieldValue }));
        validateField(fieldName as string, fieldValue);
    };

    const validateFields = () => {
        const ok = validateAll();
        if (!ok) {
            toast.error("Please fix validation errors before submitting.");
            setValidationError("Validation failed.");
            return false;
        }
        setValidationError(null);
        return true;
    };

    const handleSubmit = async (e?: React.FormEvent) => {
        if (e) e.preventDefault();
        setIsSubmitting(true);

        if (!validateFields()) {
            setIsSubmitting(false);
            return;
        }

        if (
            newTask.title &&
            newTask.title.trim() !== "" &&
            newTask.description &&
            newTask.description.trim() !== "" &&
            newTask.assignee &&
            newTask.participant
        ) {
            try {
                const createTask = async () => {
                    if (currentUser !== null) {
                        const authToken: string = await currentUser.getIdToken();
                        const newTaskId: number | null = await publishTask(authToken, newTask);
                        return newTaskId;
                    }
                    return null;
                };

                const id = await createTask();

                if (id !== null) {
                    navigate(`/task/${currentOrganization?.abbreviation}-${id}`);
                    // navigation will change page; no need to setIsSubmitting(false) here
                } else {
                    toast.error("Failed to create task. Please try again.");
                    setIsSubmitting(false);
                }
            } catch (err) {
                toast.error("Failed to create task. Please try again.");
                setIsSubmitting(false);
            }
        } else {
            // Shouldn't usually reach here because validateFields already ran, but safe guard:
            setIsSubmitting(false);
        }
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setNewTask((prev) => ({ ...prev, [name]: value }));
        validateField(name, value);
    };

    const isFormValid = () => {
        return (
            !!newTask.title &&
            newTask.title.trim() !== "" &&
            !!newTask.dueDate &&
            !!newTask.description &&
            newTask.description.trim() !== "" &&
            !!newTask.assignee &&
            !!newTask.participant &&
            Object.values(errors).every((v) => !v)
        );
    };

    const getAllParticipantUnderMe = async () => {
        if (!currentUser || !newTask.assignee) return;
        const token = await currentUser.getIdToken();
        setParticipantOptions([]); // clear list
        setLoading(prev => ({ ...prev, participant: true }));
        GetAllParticipantUnderMeAsync(token, newTask.assignee).then((res) => {
            if (res.success) {
                const formattedParticipants = res.data?.map((part) => ({
                    value: part.id.toString(),
                    label: part.firstName + " " + part.lastName,
                }));
                if (formattedParticipants) {
                    setParticipantOptions(formattedParticipants);
                }
            }
        }).finally(() => setLoading(prev => ({ ...prev, participant: false })));
    };

    const minDate = new Date().toISOString().split("T")[0];

    console.log("New task : ", newTask);


    return (
        <>
            <div className="top border_tittle">
                <div className="top_bar_tittle">
                    <h2>Create New Interim Task</h2>
                </div>
            </div>

            <Form noValidate onSubmit={handleSubmit}>
                <Row>
                    <Col md={6}>
                        <Form.Group controlId="title" className="mb-3">
                            <Form.Label>Title</Form.Label>
                            <Form.Control
                                type="text"
                                name="title"
                                placeholder="Give your task a title..."
                                value={newTask.title}
                                onChange={handleChange}
                                isInvalid={!!errors.title}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.title}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group controlId="dueDate" className="mb-3">
                            <Form.Label>Due Date</Form.Label>
                            <Form.Control
                                type="date"
                                name="dueDate"
                                value={newTask.dueDate}
                                onChange={handleDateChange}
                                isInvalid={!!errors.dueDate}
                                min={minDate}
                            />
                            <Form.Control.Feedback type="invalid">
                                {errors.dueDate}
                            </Form.Control.Feedback>
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group controlId="assignee" className="mb-3">
                            <Form.Label>Assignee</Form.Label>
                            <div>
                                <Select
                                    id="assignee"
                                    name="assignee"
                                    value={assigneeOptions.find(
                                        (option) => option.value === newTask.assignee
                                    )}
                                    onChange={(opt) => handleAssigneeChange(opt)}
                                    options={assigneeOptions}
                                    classNamePrefix="react-select"
                                    isLoading={loading.assignee}
                                    placeholder={loading.assignee ? "Loading..." : "Select.."}
                                />
                            </div>
                            {errors.assignee && (
                                <Form.Text className="text-danger">{errors.assignee}</Form.Text>
                            )}
                        </Form.Group>
                    </Col>

                    <Col md={6}>
                        <Form.Group controlId="participant" className="mb-3">
                            <Form.Label>Participant</Form.Label>
                            <div>
                                <Select
                                    id="participant"
                                    name="participant"
                                    value={
                                        participantOptions.find(option => option.value === newTask.participant)
                                        || null
                                    }
                                    onChange={(opt) => handleParticipantChange(opt)}
                                    options={participantOptions}
                                    classNamePrefix="react-select"
                                    isLoading={loading.participant}
                                    placeholder={loading.participant ? "Loading..." : "Select..."}
                                />
                            </div>
                            {errors.participant && (
                                <Form.Text className="text-danger">{errors.participant}</Form.Text>
                            )}
                        </Form.Group>
                    </Col>


                </Row>

                {newTask.assignee && !isSC && (
                    <Form.Group controlId="coAssignee" className="mb-3">
                        <Form.Label>
                            Co-Assignee <span>(Optional)</span>
                        </Form.Label>
                        <div>
                            <Select
                                id="coAssignee"
                                name="coAssignee"
                                value={assigneeOptions.find(
                                    (option) => option.value === newTask.coAssignee
                                )}
                                onChange={(opt) => handleCoAssigneeChange(opt)}
                                options={assigneeOptions?.filter(
                                    (user) => user.value !== newTask.assignee
                                )}
                                classNamePrefix="react-select"
                            />
                        </div>
                    </Form.Group>
                )}

                <Form.Group controlId="description" className="mb-3">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                        as="textarea"
                        rows={4}
                        name="description"
                        placeholder="A brief description of what the task entails..."
                        value={newTask.description}
                        onChange={handleChange}
                        isInvalid={!!errors.description}
                    />
                    <Form.Control.Feedback type="invalid">
                        {errors.description}
                    </Form.Control.Feedback>
                </Form.Group>

                <div className="form_btn_flx mby_t_24">
                    <Button
                        variant="primary"
                        type="submit"
                        disabled={!isFormValid() || isSubmitting}
                    >
                        {isSubmitting ? (
                            <>
                                <Spinner animation="border" size="sm" className="me-2" />Creating...
                            </>
                        ) : (
                            "Create Task"
                        )}
                    </Button>
                </div>
            </Form>
        </>
    );
};

export default TaskCreation;
